import type { LucideIcon } from "lucide-react";

import type { Notification, NotificationConfigContext } from "../types";
import type { UpdatesInboxMode } from "../utils/timeWindow";
import {
  NotificationCaughtUpEmpty,
  type NotificationCaughtUpMarkAll,
} from "./NotificationCaughtUpEmpty";
import { NotificationObjectMosaic } from "./NotificationObjectMosaic";
import { NotificationSearchEmpty } from "./NotificationSearchEmpty";

export function NotificationCategoryView({
  notifications,
  context,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onFetchNextPage,
  onBack,
  searchValue,
  onSearchChange,
  surfaceLabel = "Updates",
  surfaceClasses,
  surfaceIcon,
  markAllAck = null,
  inboxMode = "unread",
  onSeeEarlier,
}: {
  notifications: Notification[];
  context: NotificationConfigContext;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onFetchNextPage: () => void;
  onBack: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Eyebrow on the caught-up mosaic tile (usually the category label). */
  surfaceLabel?: string;
  surfaceClasses: { bg: string; text: string; ring: string };
  surfaceIcon?: LucideIcon;
  markAllAck?: NotificationCaughtUpMarkAll | null;
  inboxMode?: UpdatesInboxMode;
  onSeeEarlier?: () => void;
}) {
  const hasSearch = Boolean((searchValue ?? "").trim()) && !markAllAck;
  // Force empty tile during mark-all so mosaic tiles / CaughtUp don't fight.
  const list = markAllAck ? [] : notifications;
  const loading = markAllAck ? false : isLoading;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <NotificationObjectMosaic
        notifications={list}
        context={context}
        hasNextPage={markAllAck ? false : hasNextPage}
        isFetchingNextPage={markAllAck ? false : isFetchingNextPage}
        isLoading={loading}
        onFetchNextPage={onFetchNextPage}
        empty={
          hasSearch ? (
            <NotificationSearchEmpty
              caption="Try a different search, or clear it to see updates in this category again."
              onClearSearch={() => onSearchChange?.("")}
            />
          ) : (
            <NotificationCaughtUpEmpty
              eyebrow={surfaceLabel}
              caption="Nothing left to review in this category right now. Go back when you're ready."
              markAllAck={markAllAck}
              onBack={onBack}
              inboxMode={inboxMode}
              onSeeEarlier={onSeeEarlier}
              classes={surfaceClasses}
              icon={surfaceIcon}
            />
          )
        }
      />
    </div>
  );
}
